/* Types */
import { AxiosInstance } from 'axios'

import {
  BasketState,
  DateTimeChoices,
  SetTimePayload,
  TimeState,
} from '@/store/basket/basket.types'

import {
  Basket,
  DeleteGiftPayload,
  GiftPayload,
  SetDeliveryMethodPayload,
} from '@/api/modules/basket/types'
import {
  HttpStatusCode,
  ServerError,
  Success,
  UnprocessableEntityError,
} from '@/api/results'
import { ChangeChoices } from '@/types/api/api-v3/basket'
import { BasketPoint } from '@/types/api/api-v3/basket-points'
import { BasketPayments, SavedCard } from '@/types/api/api-v3/paymentMethods'
import { Mode } from '@/types/main'

import { getMappedBasket } from '@/api/modules/basket/lib'

export function createBasketModule({
  axios,
  sentry,
  city,
  hostname,
}: {
  axios: AxiosInstance
  sentry: any
  city: string
  hostname: string
}) {
  // Формируем новый url для v4
  const { API_BASE_URL, API_BASE_URL_KG, BRAND_UUID, MODE, VARITI_TOKEN } =
    useRuntimeConfig().public

  const API_URL = getApiUrl(
    API_BASE_URL,
    API_BASE_URL_KG,
    MODE as Mode,
    hostname,
  )

  const baseUrlV4 = `${API_URL}/v4/${BRAND_UUID}/${city}`

  return {
    async fetchBasket(state: BasketState, isMobile: boolean) {
      try {
        const { data } = await axios.get<Basket>(`/${city}/basket/`, {
          headers: {
            'Marketing-Section': isMobile ? 'new_site_mobile' : 'new_site',
          },
        })

        return new Success(getMappedBasket(data, state, { isFetch: true }))
      } catch (error) {
        sentry.captureException(error)
        switch (error.response?.status) {
          case HttpStatusCode.INTERNAL_SERVER_ERROR:
            return new ServerError('Что-то пошло не так')
          default:
            sentry.captureException(error)
            return new Error('Что-то пошло не так')
        }
      }
    },

    async addProduct(
      body: { product_id: number; quantity: number },
      requestSettings: { headers: object },
    ) {
      try {
        const { data } = await axios.post<Basket>(
          `/${city}/basket/add_product/`,
          body,
          requestSettings,
        )

        return new Success(data)
      } catch (error) {
        sentry.captureException(error)
        throw error
      }
    },

    async setProduct(
      state: BasketState,
      bodyRequest: { product_id: number; quantity: number },
      isMobile: boolean,
    ) {
      try {
        const { data } = await axios.post<Basket>(
          `/${city}/basket/set_product/`,
          bodyRequest,
          {
            headers: {
              'Marketing-Section': isMobile ? 'new_site_mobile' : 'new_site',
            },
          },
        )

        return new Success(getMappedBasket(data, state))
      } catch (error) {
        switch (error.response?.status) {
          case HttpStatusCode.UNPROCESSABLE_ENTITY:
            return new UnprocessableEntityError({
              message: 'Ошибка бизнес логики',
              data: error.response.data,
            })
          default:
            sentry.captureException(error)
            return new Error('Что-то пошло не так')
        }
      }
    },
    async clear(state: BasketState, isMobile: boolean) {
      try {
        const { data } = await axios.post<Basket>(
          `/${city}/basket/clear/`,
          {},
          {
            headers: {
              'Marketing-Section': isMobile ? 'new_site_mobile' : 'new_site',
            },
          },
        )

        return new Success(getMappedBasket(data, state))
      } catch (error) {
        switch (error.response?.status) {
          default:
            sentry.captureException(error)
            return new Error('Что-то пошло не так')
        }
      }
    },
    async resetPromocode(state: BasketState, isMobile: boolean) {
      try {
        const { data } = await axios.post<Basket>(
          `/${city}/basket/reset_promo_code/`,
          {},
          {
            headers: {
              'Marketing-Section': isMobile ? 'new_site_mobile' : 'new_site',
            },
          },
        )

        return new Success(getMappedBasket(data, state))
      } catch (error) {
        switch (error.response?.status) {
          default:
            sentry.captureException(error)
            return new Error('Что-то пошло не так')
        }
      }
    },
    async orderCheckout(
      bodyRequest: {
        number_of_persons?: number | null
        payment_method: string | null | undefined
        notes: string
        change: number | null
      },
      isMobile: boolean,
    ) {
      try {
        const { data } = await axios.post(
          `/${city}/basket/order_checkout/`,
          bodyRequest,
          {
            headers: {
              'Marketing-Section': isMobile ? 'new_site_mobile' : 'new_site',
            },
          },
        )
        return new Success(data.order_id)
      } catch (error) {
        switch (error.response?.status) {
          case HttpStatusCode.UNPROCESSABLE_ENTITY:
            return new UnprocessableEntityError({
              message: 'Ошибка бизнес логики',
              data: error.response.data,
            })
          default:
            sentry.captureException(error)
            return new Error('Что-то пошло не так')
        }
      }
    },
    async getPaymentMethods(isMobile: boolean) {
      try {
        const { data } = await axios.get<BasketPayments>(
          `${baseUrlV4}/basket/payment_data/`,
          {
            headers: {
              'Marketing-Section': isMobile ? 'new_site_mobile' : 'new_site',
              ...getAxiosGlobalHeader({ varitiToken: VARITI_TOKEN }),
            },
          },
        )

        return new Success(data)
      } catch (error) {
        switch (error.response?.status) {
          case HttpStatusCode.INTERNAL_SERVER_ERROR:
            return new ServerError('Что-то пошло не так')
          default:
            sentry.captureException(error)
            return new Error('Что-то пошло не так')
        }
      }
    },

    async getPaymentSaveCard(deliveryMethod: any) {
      try {
        const domen2level = hostname.split('.').slice(-2).join('.')

        const { data } = await axios.get<SavedCard[]>(
          `https://api.${domen2level}/v3/online_payments/pay_selection_cards/`,
          {
            params: deliveryMethod,
          },
        )

        return new Success(data)
      } catch (error) {
        switch (error.response?.status) {
          default:
            return new ServerError(
              error.response?.data?.detail || 'Что-то пошло не так',
            )
        }
      }
    },

    async fetchChange(amount: number) {
      try {
        const { data } = await axios.get<ChangeChoices[]>(
          `/${city}/basket/change_choices/`,
          {
            params: { amount },
          },
        )

        return new Success(data)
      } catch (error) {
        switch (error.response?.status) {
          default:
            sentry.captureException(error)
            return new Error('Что-то пошло не так')
        }
      }
    },
    async applyPromocode(
      state: BasketState,
      bodyRequest: { value: string },
      isMobile: boolean,
    ) {
      try {
        const { data } = await axios.post<Basket>(
          `/${city}/basket/apply_promo_code/`,
          bodyRequest,
          {
            headers: {
              'Marketing-Section': isMobile ? 'new_site_mobile' : 'new_site',
            },
          },
        )

        return new Success(getMappedBasket(data, state))
      } catch (error) {
        switch (error.response?.status) {
          case HttpStatusCode.UNPROCESSABLE_ENTITY:
            return new UnprocessableEntityError({
              message: 'Промокод не найден',
              data: error.response.data.value,
            })
          default:
            sentry.captureException(error)
            return new Error('Что-то пошло не так')
        }
      }
    },
    async applyBonuses(
      state: BasketState,
      bodyRequest: { amount: string | number },
      isMobile: boolean,
    ) {
      try {
        const { data } = await axios.post<Basket>(
          `/${city}/basket/set_spent_bonuses/`,
          bodyRequest,
          {
            headers: {
              'Marketing-Section': isMobile ? 'new_site_mobile' : 'new_site',
            },
          },
        )

        return new Success(getMappedBasket(data, state))
      } catch (error) {
        switch (error.response?.status) {
          case HttpStatusCode.UNPROCESSABLE_ENTITY:
            return new UnprocessableEntityError({
              message: 'Бонусы не найдены',
              data: error.response.data.value,
            })
          default:
            sentry.captureException(error)
            return new Error('Что-то пошло не так')
        }
      }
    },

    async getAvailableTime(parameters: SetTimePayload) {
      try {
        const { data } = await axios.get<TimeState>(
          `/${city}/basket/available_time/`,
          {
            params: parameters,
          },
        )

        return new Success(data)
      } catch (error) {
        switch (error.response?.status) {
          case HttpStatusCode.UNPROCESSABLE_ENTITY:
            return new UnprocessableEntityError({
              message:
                error.response.data.address_id ||
                error.response.data.delivery_at ||
                error.response.data.detail,
              data: error.response.data,
            })
          default:
            sentry.captureException(error)
            return new ServerError('Что-то пошло не так')
        }
      }
    },

    async getDateChoices() {
      try {
        const { data } = await axios.get<DateTimeChoices[]>(
          `/${city}/basket/date_choices/`,
        )

        return new Success(data)
      } catch (error) {
        switch (error.response?.status) {
          default:
            sentry.captureException(error)
            return new Error('Что-то пошло не так')
        }
      }
    },

    async getPoints() {
      try {
        const { data } = await axios.get<BasketPoint[]>(
          `/${city}/basket/points/`,
        )

        return new Success(data)
      } catch (error) {
        switch (error.response?.status) {
          default:
            sentry.captureException(error)
            return new Error('Что-то пошло не так')
        }
      }
    },
    async getTimeChoices({ date }: { date: string }) {
      try {
        const { data } = await axios.get<TimeState>(
          `/${city}/basket/time_choices/`,
          {
            params: { date },
          },
        )

        return new Success(data)
      } catch (error) {
        switch (error.response?.status) {
          case HttpStatusCode.UNPROCESSABLE_ENTITY:
            return new UnprocessableEntityError({
              message:
                error.response.data.address_id ||
                error.response.data.delivery_at,
              data: error.response.data,
            })
          default:
            sentry.captureException(error)
            return new Error('Что-то пошло не так')
        }
      }
    },

    async setTime(
      state: BasketState,
      bodyRequest: SetTimePayload,
      isMobile: boolean,
    ) {
      try {
        const { data } = await axios.post(
          `/${city}/basket/set_time/`,
          bodyRequest,
          {
            headers: {
              'Marketing-Section': isMobile ? 'new_site_mobile' : 'new_site',
            },
          },
        )

        return new Success(getMappedBasket(data, state))
      } catch (error) {
        switch (error.response?.status) {
          case HttpStatusCode.UNPROCESSABLE_ENTITY:
            return new UnprocessableEntityError({
              message:
                error.response.data.address_id ||
                error.response.data.delivery_at,
              data: error.response.data,
            })
          default:
            sentry.captureException(error)
            return new Error('Что-то пошло не так')
        }
      }
    },
    async setDeliveryMethod(
      state: BasketState,
      bodyRequest: SetDeliveryMethodPayload,
      isMobile: boolean,
    ) {
      try {
        const { data } = await axios.post<Basket>(
          `/${city}/basket/set_delivery_method/`,
          bodyRequest,
          {
            headers: {
              'Marketing-Section': isMobile ? 'new_site_mobile' : 'new_site',
            },
          },
        )

        return new Success(getMappedBasket(data, state))
      } catch (error) {
        switch (error.response?.status) {
          case HttpStatusCode.UNPROCESSABLE_ENTITY:
            return new UnprocessableEntityError({
              message:
                error.response.data.address_id ||
                error.response.data.delivery_at,
              data: error.response.data,
            })
          default:
            sentry.captureException(error)
            return new Error('Что-то пошло не так')
        }
      }
    },
    async setNumberOfPersons(
      state: BasketState,
      bodyRequest: { number: number },
      isMobile: boolean,
    ) {
      try {
        const { data } = await axios.post(
          `/${city}/basket/set_number_of_persons/`,
          bodyRequest,
          {
            headers: {
              'Marketing-Section': isMobile ? 'new_site_mobile' : 'new_site',
            },
          },
        )

        return new Success(getMappedBasket(data, state))
      } catch (error) {
        switch (error.response?.status) {
          default:
            sentry.captureException(error)
            return new Error('Что-то пошло не так')
        }
      }
    },
    async setGift(state: BasketState, bodyRequest: GiftPayload) {
      try {
        const { data } = await axios.post(`/${city}/basket/gift/`, bodyRequest)

        return new Success(getMappedBasket(data, state))
      } catch (error) {
        const errorMessage = `Не удалось добавить подарок: ${error}`

        sentry.captureException(errorMessage)

        return new ServerError(errorMessage)
      }
    },
    async deleteGift(state: BasketState, bodyRequest: DeleteGiftPayload) {
      try {
        const { data } = await axios.delete(`/${city}/basket/gift/`, {
          data: bodyRequest,
        })

        return new Success(getMappedBasket(data, state))
      } catch (error) {
        const errorMessage = `Не удалось удалить подарок: ${error}`

        sentry.captureException(errorMessage)

        return new ServerError(errorMessage)
      }
    },
    async setProposition(
      state: BasketState,
      bodyRequest: { proposition_item_id: number; quantity?: number },
      isMobile: boolean,
    ) {
      try {
        const { data } = await axios.post(
          `/${city}/basket/set_proposition/`,
          bodyRequest,
          {
            headers: {
              'Marketing-Section': isMobile ? 'new_site_mobile' : 'new_site',
            },
          },
        )

        return new Success(
          getMappedBasket(data, state, { disableMapOfProposition: true }),
        )
      } catch (error) {
        switch (error.response?.status) {
          default:
            sentry.captureException(error)
            return new Error('Что-то пошло не так')
        }
      }
    },
  }
}
